// File: src/pages/DeviceRegistry.jsx
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Badge,
} from "reactstrap";
import swal from "sweetalert";

import {
  Device_List,
  Device_Register,
  Device_Update_Status,
  Device_Delete,
  Device_Logs,
} from "../import/ApiEndPoint/Api";

import { baseURL } from "../import/ApiEndPoint/TestApi";
import { CheckPermission } from "../import/CheckPermission";

/* ===== Inline CSS injector ===== */
const injectOnce = () => {
  if (document.getElementById("device-registry-inline-css")) return;
  const s = document.createElement("style");
  s.id = "device-registry-inline-css";
  s.innerHTML = `
    .dev-toolbar {
      border: 1px solid #e5e7eb;
      background: #f4f7f6;
      border-radius: 14px;
      padding: 6px 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
      gap: 8px;
      flex-wrap: wrap;
    }
    .dev-toolbar-left {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .dev-toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .dev-search-input {
      max-width: 260px;
    }
    .dev-pill {
      padding: 2px 10px;
      font-size: 11px;
      border-radius: 999px;
      background: #e5e7eb;
    }
    .dev-table-wrapper {
      max-height: calc(100vh - 220px);
      overflow: auto;
    }
    .dev-table thead th {
      position: sticky;
      top: 0;
      background: #f9fafb;
      z-index: 1;
      font-size: 12px;
    }
    .dev-table td, .dev-table th {
      padding: 6px 8px !important;
      font-size: 12px;
      vertical-align: middle !important;
    }
  `;
  document.head.appendChild(s);
};

/* ===== API base + helpers ===== */
const API_BASE = baseURL || process.env.REACT_APP_API_BASE || "";

const makeUrl = (path) =>
  path.startsWith("http")
    ? path
    : `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const DeviceRegistry = () => {
  const [devices, setDevices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    systemName: "",
    osSerialNumber: "",
    systemNumber: "",
    ipAddress: "",
    latitude: "",
    longitude: "",
    deviceKey: "",
    notes: "",
  });

  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logDeviceName, setLogDeviceName] = useState("");

  /* NEW: loading state for geo/IP fetch */
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    injectOnce();
    fetchDevices();
  }, []);

  useEffect(() => {
    applyFilter(search);
  }, [search, devices]);

  const fetchDevices = async () => {
    try {
      setLoading(true);

      const res = await fetch(makeUrl(Device_List), {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error("Failed to load devices");
      }

      const json = await res.json();
      const list = Array.isArray(json) ? json : json?.data || [];

      setDevices(list);
      setFiltered(list);
    } catch (err) {
      console.error(err);
      swal("Error", "Failed to load devices", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (value) => {
    const v = (value || "").toLowerCase();
    if (!v) {
      setFiltered(devices);
      return;
    }
    const f = devices.filter((d) => {
      return (
        (d.systemName || "").toLowerCase().includes(v) ||
        (d.osSerialNumber || "").toLowerCase().includes(v) ||
        (d.systemNumber || "").toLowerCase().includes(v) ||
        (d.ipAddress || "").toLowerCase().includes(v) ||
        (d.registeredByName || "").toLowerCase().includes(v) ||
        (d.status || "").toLowerCase().includes(v)
      );
    });
    setFiltered(f);
  };

  const toggleModal = () => {
    setModalOpen((p) => !p);
    if (!modalOpen) {
      setForm({
        systemName: "",
        osSerialNumber: "",
        systemNumber: "",
        ipAddress: "",
        latitude: "",
        longitude: "",
        deviceKey: "",
        notes: "",
      });
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateDevice = async (e) => {
    e.preventDefault();
    if (!form.systemName || !form.deviceKey) {
      swal("Warning", "System Name and Device Key are required", "warning");
      return;
    }
    try {
      setSaving(true);

      const body = {
        ...form,
        latitude: form.latitude ? Number(form.latitude) : undefined,
        longitude: form.longitude ? Number(form.longitude) : undefined,
      };

      const res = await fetch(makeUrl(Device_Register), {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(body),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.message || "Failed to register device.");
      }

      swal("Success", "Device registered successfully", "success");
      toggleModal();
      fetchDevices();
    } catch (err) {
      console.error(err);
      swal("Error", err.message || "Failed to register device.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusToggle = async (id, status) => {
    const newStatus = status === "ACTIVE" ? "BLOCKED" : "ACTIVE";
    const confirm = await swal({
      title: `Change status to ${newStatus}?`,
      icon: "warning",
      buttons: ["Cancel", "Yes"],
      dangerMode: newStatus === "BLOCKED",
    });
    if (!confirm) return;

    try {
      const url = makeUrl(`${Device_Update_Status}${id}`);

      const res = await fetch(url, {
        method: "PATCH",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ status: newStatus }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.message || "Failed to update status");
      }

      swal("Updated", "Status updated", "success");
      fetchDevices();
    } catch (err) {
      console.error(err);
      swal("Error", err.message || "Failed to update status", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await swal({
      title: "Delete this device?",
      text: "This will remove the system from allowed devices.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!confirm) return;

    try {
      const url = makeUrl(`${Device_Delete}${id}`);

      const res = await fetch(url, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.message || "Failed to delete device");
      }

      swal("Deleted", "Device deleted", "success");
      fetchDevices();
    } catch (err) {
      console.error(err);
      swal("Error", err.message || "Failed to delete device", "error");
    }
  };

  const openLogs = async (device) => {
    try {
      setLogsLoading(true);
      setLogModalOpen(true);
      setLogDeviceName(device.systemName || "Device");

      const url = makeUrl(`${Device_Logs}${device._id}`);

      const res = await fetch(url, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error("Failed to load logs");
      }

      const json = await res.json();
      const list = Array.isArray(json) ? json : json?.data || [];

      setLogs(list);
    } catch (err) {
      console.error(err);
      swal("Error", err.message || "Failed to load logs", "error");
    } finally {
      setLogsLoading(false);
    }
  };

  /* ===================== NEW: Fetch Lat/Long + IPv4 ===================== */
  const handleFetchGeo = async () => {
    if (!("geolocation" in navigator)) {
      swal(
        "Not supported",
        "Geolocation is not supported in this browser.",
        "warning"
      );
      return;
    }

    try {
      setGeoLoading(true);

      // Wrap geolocation in a Promise for async/await
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords || {};
      const latFixed = latitude != null ? latitude.toFixed(8) : "";
      const lonFixed = longitude != null ? longitude.toFixed(8) : "";

      // Update form with 8-decimal values
      setForm((prev) => ({
        ...prev,
        latitude: latFixed,
        longitude: lonFixed,
      }));

      // Fetch IPv4 address from api.ipify.org
      try {
        const res = await fetch("https://api.ipify.org?format=json");
        if (res.ok) {
          const data = await res.json();
          if (data?.ip) {
            setForm((prev) => ({
              ...prev,
              ipAddress: data.ip,
            }));
          }
        }
      } catch (ipErr) {
        console.error("Error fetching IP address:", ipErr);
        // Don't block the flow, just log
      }
    } catch (err) {
      console.error("Geolocation error:", err);
      swal(
        "Error",
        "Unable to fetch latitude/longitude. Please allow location permission.",
        "error"
      );
    } finally {
      setGeoLoading(false);
    }
  };
  /* ===================================================================== */

  return (
    <div className="p-1">
      <Row>
        <Col md="12">
          <Card>
            <CardBody>
              <div className="dev-toolbar">
                <div className="dev-toolbar-left">
                  <h6 className="mb-0">Trusted Systems</h6>
                  <span className="dev-pill">
                    Total: {filtered.length || 0}
                  </span>
                </div>
                <div className="dev-toolbar-right">
                  <Input
                    bsSize="sm"
                    className="dev-search-input"
                    placeholder="Search system / IP / user..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {CheckPermission("Device Registry", "CREATE") && (
                    <Button color="primary" size="sm" onClick={toggleModal}>
                      + Add Device
                    </Button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="text-center p-3">
                  <Spinner size="sm" /> Loading devices...
                </div>
              ) : (
                <div className="dev-table-wrapper">
                  <Table bordered hover responsive className="dev-table mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>System Name</th>
                        <th>OS Serial / System No.</th>
                        <th>IP</th>
                        <th>Geo</th>
                        <th>Status</th>
                        <th>Registered By</th>
                        <th>Last Seen</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center">
                            No devices found.
                          </td>
                        </tr>
                      ) : (
                        filtered.map((d, idx) => (
                          <tr key={d._id || idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <div>{d.systemName}</div>
                              <div
                                style={{
                                  fontSize: "10px",
                                  color: "#6b7280",
                                }}
                              >
                                Key: ****
                              </div>
                            </td>
                            <td style={{ fontSize: "11px" }}>
                              {d.osSerialNumber || "-"}
                              <br />
                              {d.systemNumber || "-"}
                            </td>
                            <td>{d.ipAddress || "-"}</td>
                            <td style={{ fontSize: "11px" }}>
                              {d.latitude && d.longitude
                                ? `${d.latitude}, ${d.longitude}`
                                : "-"}
                            </td>
                            <td>
                              <Badge
                                color={
                                  d.status === "ACTIVE" ? "success" : "danger"
                                }
                              >
                                {d.status || "-"}
                              </Badge>
                            </td>
                            <td style={{ fontSize: "11px" }}>
                              {d.registeredByName || "-"}
                              <br />
                              <span style={{ color: "#9ca3af" }}>
                                {d.createdAt
                                  ? new Date(d.createdAt).toLocaleString()
                                  : ""}
                              </span>
                            </td>
                            <td style={{ fontSize: "11px" }}>
                              {d.lastSeenAt
                                ? new Date(d.lastSeenAt).toLocaleString()
                                : "-"}
                              <br />
                              <span style={{ color: "#9ca3af" }}>
                                {d.lastSeenIp || ""}
                              </span>
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <Button
                                  size="sm"
                                  color="info"
                                  outline
                                  onClick={() => openLogs(d)}
                                >
                                  Logs
                                </Button>

                                {CheckPermission(
                                  "Device Registry",
                                  "UPDATE"
                                ) && (
                                  <Button
                                    size="sm"
                                    color={
                                      d.status === "ACTIVE"
                                        ? "danger"
                                        : "success"
                                    }
                                    onClick={() =>
                                      handleStatusToggle(d._id, d.status)
                                    }
                                  >
                                    {d.status === "ACTIVE"
                                      ? "Block"
                                      : "Unblock"}
                                  </Button>
                                )}

                                {CheckPermission(
                                  "Device Registry",
                                  "DELETE"
                                ) && (
                                  <Button
                                    size="sm"
                                    color="secondary"
                                    outline
                                    onClick={() => handleDelete(d._id)}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add Device Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="md">
        <ModalHeader toggle={toggleModal}>Register New System</ModalHeader>
        <Form onSubmit={handleCreateDevice}>
          <ModalBody>
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>System Name*</Label>
                  <Input
                    name="systemName"
                    value={form.systemName}
                    onChange={handleFormChange}
                    bsSize="sm"
                    placeholder="e.g. ACCOUNTS-PC-1"
                    required
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>OS Serial No</Label>
                  <Input
                    name="osSerialNumber"
                    value={form.osSerialNumber}
                    onChange={handleFormChange}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>System / Asset No</Label>
                  <Input
                    name="systemNumber"
                    value={form.systemNumber}
                    onChange={handleFormChange}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Fixed IP (optional)</Label>
                  <Input
                    name="ipAddress"
                    value={form.ipAddress}
                    onChange={handleFormChange}
                    bsSize="sm"
                    placeholder="e.g. 49.35.195.169"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Latitude</Label>
                  <Input
                    name="latitude"
                    value={form.latitude}
                    onChange={handleFormChange}
                    bsSize="sm"
                    type="number"
                    step="0.00000001"
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Longitude</Label>
                  <Input
                    name="longitude"
                    value={form.longitude}
                    onChange={handleFormChange}
                    bsSize="sm"
                    type="number"
                    step="0.00000001"
                  />
                </FormGroup>
              </Col>

              {/* NEW BUTTON: Fetch latitude, longitude & IPv4 */}
              <Col md="12">
                <Button
                  type="button"
                  size="sm"
                  color="secondary"
                  onClick={handleFetchGeo}
                  disabled={geoLoading}
                  style={{ marginBottom: 6 }}
                >
                  {geoLoading ? (
                    <>
                      <Spinner size="sm" /> Fetching...
                    </>
                  ) : (
                    "Fetch Latitude, Longitude"
                  )}
                </Button>
                <div style={{ fontSize: "10px", color: "#6b7280" }}>
                  This will auto-fill latitude, longitude (8 decimal places) and
                  your IPv4 address.
                </div>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>Device Key (secret)*</Label>
                  <Input
                    name="deviceKey"
                    value={form.deviceKey}
                    onChange={handleFormChange}
                    bsSize="sm"
                    placeholder="Paste activation key here"
                    required
                  />
                  <small style={{ fontSize: "10px", color: "#6b7280" }}>
                    This is the secret key you will configure in browser
                    localStorage on that system. It is never shown again to
                    normal users.
                  </small>
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    name="notes"
                    value={form.notes}
                    onChange={handleFormChange}
                    bsSize="sm"
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" size="sm" onClick={toggleModal}>
              Cancel
            </Button>
            <Button color="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Save"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Logs Modal */}
      <Modal
        isOpen={logModalOpen}
        toggle={() => setLogModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setLogModalOpen(false)}>
          Access Logs - {logDeviceName}
        </ModalHeader>
        <ModalBody>
          {logsLoading ? (
            <div className="text-center p-2">
              <Spinner size="sm" /> Loading logs...
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center">No logs.</div>
          ) : (
            <div style={{ maxHeight: "60vh", overflow: "auto" }}>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Time</th>
                    <th>Email</th>
                    <th>IP</th>
                    <th>Geo</th>
                    <th>Result</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, idx) => (
                    <tr key={l._id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        {l.createdAt
                          ? new Date(l.createdAt).toLocaleString()
                          : "-"}
                      </td>
                      <td>{l.email || "-"}</td>
                      <td>{l.ipAddress || "-"}</td>
                      <td style={{ fontSize: "11px" }}>
                        {l.latitude && l.longitude
                          ? `${l.latitude}, ${l.longitude}`
                          : "-"}
                      </td>
                      <td>
                        <Badge
                          color={l.result === "ALLOWED" ? "success" : "danger"}
                        >
                          {l.result}
                        </Badge>
                      </td>
                      <td style={{ fontSize: "11px" }}>{l.reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            size="sm"
            onClick={() => setLogModalOpen(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default DeviceRegistry;
